import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface ScrollableDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  header?: React.ReactNode
  footer?: React.ReactNode
  showCloseButton?: boolean
  width?: string | number
  maxWidthXs?: string
  maxWidthSm?: string
  maxWidthMd?: string
  maxWidthLg?: string
  maxWidthXl?: string
  maxWidthXxl?: string
}

const ScrollableDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  ScrollableDialogContentProps
>(({ className, children, header, footer, showCloseButton = true, width, maxWidthXs, maxWidthSm, maxWidthMd, maxWidthLg, maxWidthXl, maxWidthXxl, ...props }, ref) => {
  const internalRef = React.useRef<HTMLDivElement>(null)
  const combinedRef = React.useCallback((node: HTMLDivElement | null) => {
    internalRef.current = node
    if (typeof ref === 'function') {
      ref(node)
    } else if (ref) {
      ref.current = node
    }
  }, [ref])

  const widthStyle = width
    ? typeof width === 'number'
      ? { width: `${width}px`, maxWidth: `${width}px` }
      : { width, maxWidth: width }
    : {}

  // Build max-width classes dynamically based on props (fallback for when no custom max-width)
  const maxWidthClasses = [
    'sm:max-w-xl',
    'md:max-w-2xl',
    'lg:max-w-3xl',
    'xl:max-w-4xl',
  ].join(' ')

  // Generate unique style ID for this dialog instance
  const styleIdRef = React.useRef<string | null>(null)
  if (!styleIdRef.current) {
    styleIdRef.current = `scrollable-dialog-${Math.random().toString(36).substr(2, 9)}`
  }
  const styleId = styleIdRef.current

  const hasCustomMaxWidth = maxWidthXs || maxWidthSm || maxWidthMd || maxWidthLg || maxWidthXl || maxWidthXxl

  // Create and inject styles immediately (before paint)
  React.useLayoutEffect(() => {
    if (!hasCustomMaxWidth) return

    // Find or create style element
    let styleElement = document.getElementById(styleId) as HTMLStyleElement

    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = styleId
      styleElement.setAttribute('data-scrollable-dialog', 'true')
      document.head.appendChild(styleElement)
    }

    // Build CSS with all breakpoints
    const css = `
      ${maxWidthXs ? `.${styleId} { max-width: ${maxWidthXs} !important; }` : ''}
      ${maxWidthSm ? `@media (min-width: 640px) { .${styleId} { max-width: ${maxWidthSm} !important; } }` : ''}
      ${maxWidthMd ? `@media (min-width: 768px) { .${styleId} { max-width: ${maxWidthMd} !important; } }` : ''}
      ${maxWidthLg ? `@media (min-width: 1024px) { .${styleId} { max-width: ${maxWidthLg} !important; } }` : ''}
      ${maxWidthXl ? `@media (min-width: 1280px) { .${styleId} { max-width: ${maxWidthXl} !important; } }` : ''}
      ${maxWidthXxl ? `@media (min-width: 1600px) { .${styleId} { max-width: ${maxWidthXxl} !important; } }` : ''}
    `.trim()

    // Always update CSS to ensure it's current
    styleElement.textContent = css

    // Cleanup
    return () => {
      const styleToRemove = document.getElementById(styleId)
      if (styleToRemove && styleToRemove.parentNode) {
        styleToRemove.parentNode.removeChild(styleToRemove)
      }
    }
  }, [styleId, maxWidthXs, maxWidthSm, maxWidthMd, maxWidthLg, maxWidthXl, maxWidthXxl, hasCustomMaxWidth])

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={combinedRef}
        className={cn(
          "fixed left-[50%] overflow-hidden top-[50%] z-50 flex max-h-[95vh] w-[95vw] translate-x-[-50%] translate-y-[-50%] flex-col rounded-lg border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] ",
          !hasCustomMaxWidth && maxWidthClasses,
          hasCustomMaxWidth && styleId,
          className
        )}
        style={{ ...widthStyle, ...props.style }}
        {...props}
      >
        {/* Fixed Header */}
        {header && (
          <div className="relative flex-shrink-0 w-full flex justify-between items-start border-b bg-background px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-5">
            {header}
            {showCloseButton && (
              <DialogPrimitive.Close className="rounded-[50%] transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground  p-[5px] " style={{ backgroundColor: 'var(--secondary-color)' }}>
                <X className="h-4 w-4 text-[#fff]"  />
                {/* <span className="sr-only">Close</span> */}
              </DialogPrimitive.Close>
            )}
          </div>
        )}

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-5">
          {children}
          {/* Provide a default, visually hidden description to satisfy a11y checks */}
          <DialogPrimitive.Description className="sr-only">Dialog</DialogPrimitive.Description>
        </div>

        {/* Fixed Footer */}
        {footer && (
          <div className="flex-shrink-0 border-t bg-background px-3 sm:px-4 md:px-6 py-3 sm:py-4">
            {footer}
          </div>
        )}

        {/* Close button when no header */}
        {!header && showCloseButton && (
          <DialogPrimitive.Close className="absolute right-2 sm:right-3 md:right-4 top-2 sm:top-3 md:top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-10">
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
ScrollableDialogContent.displayName = "ScrollableDialogContent"

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 sm:space-y-2 text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-base sm:text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("hidden lg:block text-xs sm:text-sm text-muted-foreground max-w-[500px]", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  ScrollableDialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}

